import {
  closestCenter,
  type Active,
  type Announcements,
  type CollisionDetection,
  type Over,
} from '@dnd-kit/core'
import { OGSM_COPY } from '@/features/ogsm/ogsm-labels'
import { findNode, positionOf, siblingIds } from '@/features/ogsm/reorder'
import type { OgsmBoard } from '@/types/api'

/** closestCenter limited to the active node's siblings. Returns [] (no `over`) when the
 *  centre of `collisionRect` lies above the top or below the bottom of the union of the
 *  siblings' rects in `droppableRects`, so a drop on another parent's list is a no-op. */
export function siblingCollision(board: OgsmBoard): CollisionDetection {
  return (args) => {
    const siblings = siblingIds(board, String(args.active.id))
    const rects = siblings.flatMap((id) => {
      const rect = args.droppableRects.get(id)
      return rect ? [rect] : []
    })
    if (rects.length === 0) return []

    const top = Math.min(...rects.map((rect) => rect.top))
    const bottom = Math.max(...rects.map((rect) => rect.bottom))
    const centre = args.collisionRect.top + args.collisionRect.height / 2
    if (centre < top || centre > bottom) return []

    return closestCenter({
      ...args,
      droppableContainers: args.droppableContainers.filter((container) =>
        siblings.includes(String(container.id))
      ),
    })
  }
}

/** Screen-reader announcements with node titles and 1-based positions (dnd-kit's defaults read UUIDs).
 *  onDragStart → pickedUp; onDragOver with a sibling `over` → movedTo (the over node's position);
 *  onDragOver with no or a non-sibling `over` → undefined; onDragEnd with a sibling `over` → droppedAt,
 *  otherwise → cancelled (original position); onDragCancel → cancelled. Empty title → OGSM_COPY.untitled. */
export function reorderAnnouncements(board: OgsmBoard): Announcements {
  const { reorder } = OGSM_COPY
  const title = (active: Active) =>
    findNode(board, String(active.id))?.title.trim() || OGSM_COPY.untitled

  const target = (active: Active, over: Over | null) => {
    if (!over) return null
    const overId = String(over.id)
    if (!siblingIds(board, String(active.id)).includes(overId)) return null
    return positionOf(board, overId)
  }

  const cancelled = (active: Active) => {
    const from = positionOf(board, String(active.id))
    return from ? reorder.cancelled(title(active), from.index + 1) : undefined
  }

  return {
    onDragStart: ({ active }) => reorder.pickedUp(title(active)),
    onDragOver: ({ active, over }) => {
      const to = target(active, over)
      return to
        ? reorder.movedTo(title(active), to.index + 1, to.total)
        : undefined
    },
    onDragEnd: ({ active, over }) => {
      const to = target(active, over)
      return to
        ? reorder.droppedAt(title(active), to.index + 1, to.total)
        : cancelled(active)
    },
    onDragCancel: ({ active }) => cancelled(active),
  }
}
